import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert, IconButton,
  InputAdornment, Divider, ToggleButtonGroup, ToggleButton, Grid, Stack, Autocomplete,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person, Phone, School, Business, Google, Apple } from '@mui/icons-material';
import useAuthStore from '../../store/useStore';
import { ghanaAPI } from '../../api/client';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function RegisterPage() {
  const navigate = useNavigate();
  const { register, socialLogin } = useAuthStore();
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    phone: '', role: 'tenant', university: '', student_id: '', company_name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const googleBtnRef = useRef(null);
  const [universities, setUniversities] = useState([]);

  useEffect(() => {
    const fetchUnis = async () => {
      try {
        const res = await ghanaAPI.getUniversities();
        setUniversities((res.data.universities || []).map(u => `${u.name} (${u.abbreviation})`));
      } catch (_) {}
    };
    fetchUnis();
  }, []);

  const validatePassword = (pw) => {
    const errs = [];
    if (pw.length < 8) errs.push('At least 8 characters');
    if (!/[A-Z]/.test(pw)) errs.push('One uppercase letter');
    if (!/[a-z]/.test(pw)) errs.push('One lowercase letter');
    if (!/[0-9]/.test(pw)) errs.push('One digit');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pw)) errs.push('One special character');
    setPasswordErrors(errs);
    return errs.length === 0;
  };

  const redirectAfterLogin = useCallback((user) => {
    if (user?.role === 'super_admin' || user?.role === 'rent_control') {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validatePassword(form.password)) return;
    setLoading(true);
    try {
      await register(form);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (response) => {
    setLoading(true);
    setError('');
    try {
      const user = await socialLogin('google', response.credential, form.role);
      redirectAfterLogin(user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-up failed');
    } finally {
      setLoading(false);
    }
  }, [socialLogin, redirectAfterLogin, form.role]);

  const handleGoogleSignUp = () => {
    if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID === 'YOUR_GOOGLE_CLIENT_ID_HERE') {
      setError('Google Sign-In is not configured yet. Set REACT_APP_GOOGLE_CLIENT_ID in your .env file.');
      return;
    }
    /* global google */
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          if (googleBtnRef.current) {
            googleBtnRef.current.innerHTML = '';
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              theme: 'outline', size: 'large', width: '100%', text: 'signup_with',
            });
          }
        }
      });
    } else {
      setError('Google Sign-In SDK not loaded. Please refresh and try again.');
    }
  };

  const handleAppleSignUp = () => {
    setError('Apple Sign-In requires an Apple Developer account and domain verification. Configure your Apple Service ID to enable this feature.');
  };

  useEffect(() => {
    if (GOOGLE_CLIENT_ID && GOOGLE_CLIENT_ID !== 'YOUR_GOOGLE_CLIENT_ID_HERE' && !document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" fontWeight={700} color="primary" component={Link} to="/" sx={{ textDecoration: 'none' }}>
            CampusNest
          </Typography>
        </Box>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>Create Account</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Join CampusNest today</Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>I am a:</Typography>
          <ToggleButtonGroup
            value={form.role} exclusive fullWidth
            onChange={(e, val) => val && setForm({ ...form, role: val })}
            sx={{ mb: 3 }}
          >
            <ToggleButton value="tenant" sx={{ textTransform: 'none' }}>Student / Tenant</ToggleButton>
            <ToggleButton value="landlord" sx={{ textTransform: 'none' }}>Landlord</ToggleButton>
            <ToggleButton value="agent" sx={{ textTransform: 'none' }}>Agent</ToggleButton>
          </ToggleButtonGroup>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth label="First Name" required
                  value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth label="Last Name" required
                  value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth label="Email Address" type="email" required sx={{ mt: 2 }}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Phone Number" sx={{ mt: 2 }}
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start"><Phone color="action" /></InputAdornment> }}
            />
            <TextField
              fullWidth label="Password" type={showPassword ? 'text' : 'password'} required sx={{ mt: 2 }}
              value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); validatePassword(e.target.value); }}
              error={passwordErrors.length > 0 && form.password.length > 0}
              helperText={form.password.length > 0 && passwordErrors.length > 0 ? passwordErrors.join(', ') : 'Min 8 chars, upper, lower, digit, special'}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {form.role === 'tenant' && (
              <>
                <Autocomplete
                  freeSolo options={universities} value={form.university || ''}
                  onInputChange={(e, v) => setForm({ ...form, university: v })}
                  sx={{ mt: 2 }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth label="University / College"
                      placeholder="Search institution..."
                      InputProps={{ ...params.InputProps, startAdornment: <><InputAdornment position="start"><School color="action" /></InputAdornment>{params.InputProps.startAdornment}</> }}
                    />
                  )}
                />
                <TextField
                  fullWidth label="Student ID" sx={{ mt: 2 }}
                  value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}
                />
              </>
            )}

            {(form.role === 'landlord' || form.role === 'agent') && (
              <TextField
                fullWidth label="Company Name" sx={{ mt: 2 }}
                value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start"><Business color="action" /></InputAdornment> }}
              />
            )}

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ py: 1.5, mt: 3, mb: 2 }}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}><Typography variant="body2" color="text.secondary">or sign up with</Typography></Divider>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              fullWidth variant="outlined" size="large" startIcon={<Google />}
              onClick={handleGoogleSignUp} disabled={loading}
              sx={{ py: 1.2, textTransform: 'none', borderColor: '#ddd', color: '#333',
                '&:hover': { borderColor: '#4285F4', bgcolor: '#f5f5f5' } }}
            >
              Google
            </Button>
            <Button
              fullWidth variant="outlined" size="large" startIcon={<Apple />}
              disabled={loading}
              sx={{ py: 1.2, textTransform: 'none', borderColor: '#ddd', color: '#333',
                '&:hover': { borderColor: '#000', bgcolor: '#f5f5f5' } }}
              onClick={handleAppleSignUp}
            >
              Apple
            </Button>
          </Stack>
          <Box ref={googleBtnRef} sx={{ mb: 1, display: 'flex', justifyContent: 'center' }} />

          <Typography variant="body2" textAlign="center">
            Already have an account?{' '}
            <Typography component={Link} to="/login" variant="body2" color="primary" fontWeight={600} sx={{ textDecoration: 'none' }}>
              Sign In
            </Typography>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default RegisterPage;
